import { PasteCleanup, RichTextEditor, RichTextEditorModel } from '@syncfusion/ej2-richtexteditor';

import { IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_MAX_SIZE_KB } from '@/shared/constants';
import { routes } from '@/shared/routes';
import { EditableClientProps } from '@/shared/types/editable';

import { controlModel } from '@/front/editable/control';
import { input } from '@/front/editable/input';
import { addValidation, setControl } from '@/front/editable/state';
import { addKeydownListener, change } from '@/front/editable/utils';
import { alertModal } from '@/front/modal';

RichTextEditor.Inject(PasteCleanup);

export const initRichTextEditor = (p: EditableClientProps, e: HTMLElement) => {
  const richTextEditor = new RichTextEditor({
    ...richTextEditorModel(p),
    change: change(e),
  });

  richTextEditor.appendTo(input(p, e));

  const contentElement = richTextEditor.getContent() as HTMLElement;

  addKeydownListener(contentElement);

  // Listen for keydown as change event fires after dependent events
  contentElement.addEventListener('keydown', (event: any) => {
    if (event.key !== 'Escape' && event.key !== 'Tab') {
      change(e)();
    }
  });

  setControl(p.id, richTextEditor);

  addValidation(p, e);
};

export const richTextEditorModel = (p: EditableClientProps): RichTextEditorModel => ({
  ...controlModel(p),
  format: undefined,
  toolbarSettings: {
    items: !p.readOnly
      ? [
          'Bold',
          'Italic',
          'Underline',
          'FontColor',
          'ClearFormat',
          'OrderedList',
          'UnorderedList',
          'CreateLink',
          ...(p.allowImages ? ['Image'] : []),
        ]
      : [],
  },
  editorMode: 'HTML',
  enterKey: 'DIV',
  pasteCleanupSettings: {
    plainText: true,
    allowedStyleProps: [],
  },
  fontColor: {
    default: '#000000',
  },
  ...(p.allowImages
    ? {
        insertImageSettings: {
          path: `${routes.data.uploads.get(false)}/`,
          saveUrl: routes.data.uploads.put(),
          // HACK Workaround for bug in EJ2 uploader. It sets file input accept attribute to e.g. ".png.jpg.jpeg" with
          // no commas. Therefore we have to include the commas in the array below. This should still work if and when
          // they fix this, i.e. the browser should still accept ".png,,.jpg,,.jpeg".
          allowedTypes: IMAGE_UPLOAD_ACCEPT.map((accept) => `${accept},`),
        },
        imageUploading: (args: { fileData: { size: number }; cancel: boolean }) => {
          if (args.fileData.size > IMAGE_UPLOAD_MAX_SIZE_KB * 1024) {
            args.cancel = true;
            alertModal(`File exceeds maximum size of ${IMAGE_UPLOAD_MAX_SIZE_KB}KB`);
          }
        },
        imageUploadSuccess: (args: any) => {
          // https://ej2.syncfusion.com/documentation/rich-text-editor/how-to/rename-images-in-server
          if (args.e.currentTarget.getResponseHeader('name') != null) {
            args.file.name = args.e.currentTarget.getResponseHeader('name');
            const filename: any = document.querySelectorAll('.e-file-name')[0];
            filename.innerHTML = args.file.name.replace(document.querySelectorAll('.e-file-type')[0].innerHTML, '');
            filename.title = args.file.name;
          }
        },
      }
    : {}),
});
